<template lang="pug">
    .member-search
        .search-input-wrapper
            el-input.is-full-width(
                ref="input"
                v-model="searchQuery"
                :placeholder="placeholder"
                size="medium"
                @input="onSearchInput"
                @focus="onFocus"
                @blur="onBlur"
            )
            button.keyboard-toggle(
                type="button"
                :class="{ active: showKeyboard }"
                @click.prevent.stop="toggleKeyboard"
            )
                i.el-icon-fa-keyboard.far
        .dropdown-wrapper(v-if="showDropdown")
            .dropdown-list(v-loading="loading" v-dragscroll)
                .dropdown-item(
                    v-for="member in options"
                    :key="member.id"
                    @click="onSelect(member)"
                )
                    .member-option(:class="{ 'show-details': showDetails }")
                        .member-header
                            span.nr(v-if="getMemberNr(member)") \#{{ getMemberNr(member) }}
                            span.salutation(v-if="showDetails && member.gender") {{ formatGender(member.gender) }}
                            span.name {{ member.first_name }} {{ member.last_name }}
                        .email {{ member.email }}
                .dropdown-empty(v-if="!loading && options.length === 0 && searchQuery.length >= 2")
                    | {{ $t('No results') }}
        .keyboard-wrapper(v-if="showKeyboard")
            Keyboard(
                v-model="searchQuery"
                @change="handleKeyboardChange"
                @key-press="handleKeyboardKeyPress"
            )
</template>

<i18n>
{
    "de": {
        "No results": "Keine Ergebnisse"
    }
}
</i18n>

<script>
import _ from 'lodash';
import MemberApi from '@/extensions/restrictedArea/store/api/member';
import { useSetupStore } from '@/stores';
import Keyboard from '@/components/Keyboard';

export default {
    name: 'MemberSearch',
    components: {
        Keyboard,
    },
    props: {
        value: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: 'Search member...'
        },
        clearable: {
            type: Boolean,
            default: false
        },
        showDetails: {
            type: Boolean,
            default: false
        },
        excludeIds: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            options: [],
            searchQuery: '',
            showKeyboard: false,
            dropdownVisible: false,
            isSelecting: false
        };
    },
    computed: {
        setupStore() {
            return useSetupStore();
        },
        cmsId() {
            return _.get(this.setupStore, 'activeCms.id');
        },
        showDropdown() {
            // Show dropdown when we have results or are loading, and either dropdown is visible or keyboard is open
            const hasContent = this.loading || this.options.length > 0 || this.searchQuery.length >= 2;
            return hasContent && (this.dropdownVisible || this.showKeyboard);
        }
    },
    watch: {
        value: {
            handler(val) {
                if (val && !this.isSelecting) {
                    this.isSelecting = true;
                    this.searchQuery = this.formatMemberLabel(val);
                    this.$nextTick(() => {
                        this.isSelecting = false;
                    });
                }
            },
            immediate: true
        },
        searchQuery(query) {
            // Don't trigger search when programmatically setting value after selection
            if (this.isSelecting) {
                return;
            }
            if (query && query.length >= 2) {
                this.dropdownVisible = true;
                this.search(query);
            } else {
                this.options = [];
            }
        }
    },
    methods: {
        search: _.debounce(function(query) {
            if (!query || query.length < 2) {
                return;
            }

            this.loading = true;
            MemberApi.autocomplete(this.cmsId, query)
                .then(members => {
                    // Filter out excluded members (compare as strings to handle type mismatches)
                    const excludeSet = new Set(this.excludeIds.map(id => String(id)));
                    const filtered = (members || []).filter(m => !excludeSet.has(String(m.id)));
                    this.options = filtered;
                })
                .catch(err => {
                    console.error('Error searching members:', err);
                    this.options = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        }, 300),
        formatMemberLabel(member) {
            if (!member) return '';
            const name = [member.first_name, member.last_name].filter(Boolean).join(' ');
            const nr = this.getMemberNr(member);
            const nrPrefix = nr ? `#${nr} ` : '';
            return name ? `${nrPrefix}${name} (${member.email})` : member.email;
        },
        getMemberNr(member) {
            return _.get(member, 'customer.nr') || member.nr;
        },
        formatGender(gender) {
            const genderMap = {
                'M': 'Herr',
                'F': 'Frau',
                'X': ''
            };
            return genderMap[gender] || '';
        },
        onSearchInput() {
            this.dropdownVisible = true;
        },
        onSelect(member) {
            this.isSelecting = true;
            this.$emit('input', member);
            this.$emit('select', member);
            this.dropdownVisible = false;
            this.options = [];
            // Clear for multi-add behavior, keep for single-select
            if (!this.clearable) {
                // Multi-add: clear input but keep keyboard open for next selection
                this.searchQuery = '';
            } else {
                // Single-select: show selection and close keyboard
                this.searchQuery = this.formatMemberLabel(member);
                this.showKeyboard = false;
            }
            this.$nextTick(() => {
                this.isSelecting = false;
            });
        },
        onFocus() {
            this.$emit('focus');
            if (this.searchQuery.length >= 2) {
                this.dropdownVisible = true;
            }
        },
        onBlur() {
            this.$emit('blur');
            // Delay hiding dropdown to allow click on items
            setTimeout(() => {
                if (!this.showKeyboard) {
                    this.dropdownVisible = false;
                }
            }, 200);
        },
        toggleKeyboard() {
            this.showKeyboard = !this.showKeyboard;
            if (this.showKeyboard && this.$refs.input) {
                this.$refs.input.focus();
            }
        },
        handleKeyboardKeyPress(button) {
            // Close keyboard on return only for single-select mode (clearable=true)
            if (this.clearable && (button === '{enter}' || button === '{return}')) {
                this.showKeyboard = false;
            }
        },
        handleKeyboardChange(input) {
            // Ensure dropdown is visible and search is triggered when using keyboard
            this.dropdownVisible = true;
            if (input && input.length >= 2) {
                this.search(input);
            } else {
                this.options = [];
            }
        }
    }
};
</script>

<style lang="sass" scoped>
.member-search
    width: 100%
    display: block
    position: relative

.search-input-wrapper
    position: relative
    width: 100%
    display: block
    &::v-deep
        .el-input,
        .el-input.is-full-width
            width: 100%
        .el-input__inner
            padding-right: 40px

.keyboard-toggle
    position: absolute
    right: 8px
    top: 50%
    transform: translateY(-50%)
    background: none
    border: none
    padding: 6px 8px
    cursor: pointer
    color: $color-grey-text
    font-size: 1.1em
    z-index: 1
    transition: color 0.2s
    &:hover
        color: $--color-primary
    &.active
        color: $--color-primary

.dropdown-wrapper
    position: absolute
    bottom: 100%
    left: 0
    right: 0
    z-index: 2000
    margin-bottom: 4px

.dropdown-list
    background: #fff
    border: 1px solid #dcdfe6
    border-radius: 4px
    box-shadow: 0 -2px 12px 0 rgba(0, 0, 0, 0.1)
    max-height: 200px
    overflow-y: auto

.dropdown-item
    padding: 4px 12px
    cursor: pointer
    transition: background 0.2s
    &:hover
        background: #f5f7fa

.dropdown-empty
    padding: 10px 12px
    color: $color-grey-text
    text-align: center

.keyboard-wrapper
    margin-top: 8px
    background: darken($color-skeleton, 10%)
    border-radius: 8px
    padding: 12px
    position: relative
    &:before
        content: ''
        position: absolute
        top: -8px
        right: 18px
        width: 0
        height: 0
        border-left: 8px solid transparent
        border-right: 8px solid transparent
        border-bottom: 8px solid darken($color-skeleton, 10%)

.member-option
    display: flex
    flex-direction: column
    gap: 2px
    line-height: 1.4
    .member-header
        display: flex
        align-items: center
        gap: 6px
        line-height: 1.3
        .nr
            font-size: 0.85em
            color: $color-grey-text
            font-weight: 500
        .salutation
            font-size: 0.85em
            color: $color-grey-text
        .name
            font-weight: 500
    .email
        font-size: 0.85em
        color: $color-grey-text
        line-height: 1.3
</style>
